import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { throwIfBlurAborted } from '@/lib/background-blur/background-blur-error';
import { waitForOperation } from '@/lib/background-blur/wait-for-operation';

import {
  type CandidateCounters,
  emptyCandidateCounters,
  extractAttemptedPairs,
  extractSelectedPair,
  fmt,
  getCallId,
  tallyCandidate,
  wlog,
} from '@/lib/webrtc-logger';
import {
  type CallSdpMessage,
  type TabletAnswerMessage,
} from '@/types/websocket-call-messages';
import {
  getUserMediaErrorInfo,
  type MediaErrorInfo,
} from './get-user-media-error-info';
import {
  type BackgroundBlurMode,
  type BackgroundBlurStatus,
  type ReplaceOutgoingVideoTrack,
  useBackgroundBlur,
} from './use-background-blur';
export type {
  BackgroundBlurMode,
  BackgroundBlurStatus,
} from './use-background-blur';

/**
 * Thrown by `switchDevices` when the user-requested device change fails.
 * `rolledBack` indicates whether the hook was able to restore the previous
 * working stream afterwards. Callers can use this to decide what feedback
 * to surface:
 *
 * - `rolledBack === true` → the stream is still live on the previous
 *   device. Show a transient toast ("couldn't switch, still using X")
 *   without the error overlay, which would otherwise be misleading.
 * - `rolledBack === false` → the stream is broken and the hook has set
 *   `mediaError`, so the place-call overlay will already explain it.
 */
export class MediaSwitchError extends Error {
  readonly rolledBack: boolean;
  readonly cause: unknown;

  constructor(cause: unknown, rolledBack: boolean) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = cause instanceof Error ? cause.name : 'MediaSwitchError';
    this.rolledBack = rolledBack;
    this.cause = cause;
  }
}

export function wasRolledBack(error: unknown): boolean {
  return error instanceof MediaSwitchError && error.rolledBack;
}

type StreamState = 'idle' | 'starting' | 'active' | 'stopping' | 'error';
type ConnectionFailureReason =
  | 'ice_disconnected_timeout'
  | 'ice_failed'
  | 'pc_failed'
  | 'connection_timeout';

interface ConnectionFailureContext {
  iceConnectionState: RTCIceConnectionState;
  peerConnectionState: RTCPeerConnectionState;
  signalingState: RTCSignalingState;
  iceGatheringState: RTCIceGatheringState;
  elapsedMsSinceOffer: number | null;
}

interface WebRTCIceOptions {
  iceServers: RTCIceServer[];
  iceTransportPolicy?: RTCIceTransportPolicy;
  iceProfileId?: string;
}

const ICE_DISCONNECT_GRACE_MS = 8000;
/**
 * Hard cap on "time from SDP answer applied to ICE connected" before
 * we declare the call failed ourselves. The backend does not enforce a
 * ringing timeout, so without this watchdog a call that can't find a
 * route sits in `checking` until the user gives up — a silent teardown
 * that produces no diagnostic log. 20s accommodates the ICE candidate
 * exchange round-tripping through the signalling WebSocket → Alexa
 * skills → device path on both sides before declaring the route dead,
 * while still beating human patience.
 */
const PRE_CONNECT_WATCHDOG_MS = 20000;
/**
 * Merge in Google STUN servers as a fallback in case the client's network environment
 * blocks Cloudflare's STUN servers.
 */
export const FALLBACK_ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

interface WebRTCMediaHook {
  isStreamReady: boolean;
  streamState: StreamState;
  videoRef: React.RefObject<HTMLVideoElement>;
  switchDevices: (constraints: MediaStreamConstraints) => Promise<void>;
  backgroundBlurEnabled: boolean;
  backgroundBlurError: string | null;
  backgroundBlurManagedBySystem: boolean;
  backgroundBlurMode: BackgroundBlurMode;
  backgroundBlurStatus: BackgroundBlurStatus;
  setBackgroundBlurEnabled: (enabled: boolean) => Promise<void>;

  /**
   * Structured info about the most recent getUserMedia failure, or null
   * if the stream is healthy. Cleared on successful (re)start.
   */
  mediaError: MediaErrorInfo | null;
  /**
   * Retry the most recent startMediaStream call using the last-used
   * constraints. Safe to call when `mediaError` is set.
   */
  retryMediaStream: () => Promise<void>;

  // WebRTC connection
  peerConnection: RTCPeerConnection | null;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
  getPeerConnection: () => RTCPeerConnection | null;
  createPeerConnection: () => RTCPeerConnection | null;
  createOffer: () => Promise<void>;
  handleAnswer: (answer: CallSdpMessage) => Promise<boolean>;
  handleTabletAnswer: (message: TabletAnswerMessage) => Promise<boolean>;

  /**
   * Accessors used by `use-call.ts` to enrich the `Call teardown` log
   * with per-call context (did we ever apply a remote SDP answer? what
   * did our local candidate gathering look like?). Read via refs so
   * callers always see the latest value without re-rendering on every
   * tally.
   */
  getAnswerReceived: () => boolean;
  getLocalCandidateCounts: () => CandidateCounters;

  cleanup: () => void;
}

/**
 * Take a fingerprint of the ICE server configuration that we can use to determine
 * if the selected URLs have changed since the last peer connection was created.
 */
function iceServersFingerprint(servers: RTCIceServer[]): string {
  const urls = servers.flatMap((server) =>
    Array.isArray(server.urls) ? server.urls : [server.urls],
  );
  return JSON.stringify([...urls].sort());
}

/**
 * This hook manages local media (camera and microphone) and WebRTC peer connections.
 * It provides methods to start/stop the camera, create a peer connection,
 * create an offer, and handle incoming answers.
 *
 * The only thing we need to pass in is a callback that will be called
 * when the offer is ready to be sent. This is because we need to wait for
 * the ICE gathering to complete before sending the offer. This will be sent over
 * the WebSocket connection.
 */
export function useWebRTCMedia(
  onOfferReady?: (offer: RTCSessionDescription) => void,
  isActive = false,
  iceOptionsOrServers: WebRTCIceOptions | RTCIceServer[] = { iceServers: [] },
  onConnectionFailure?: (
    reason: ConnectionFailureReason,
    context: ConnectionFailureContext,
  ) => void,
): WebRTCMediaHook {
  const iceOptions = useMemo<WebRTCIceOptions>(
    () =>
      Array.isArray(iceOptionsOrServers)
        ? { iceServers: iceOptionsOrServers }
        : iceOptionsOrServers,
    [iceOptionsOrServers],
  );
  /**
   * These refs are used to manage the media stream and its state.
   * The `streamOperationAborterRef` is used to abort any in-flight operations
   * when starting or stopping the media stream, which prevents the hook from
   * retaining access to the camera and causing the camera light to stay on.
   */
  const streamOperationAborterRef = useRef<AbortController>();
  const rawStreamRef = useRef<MediaStream | null>(null);
  const outboundStreamRef = useRef<MediaStream | null>(null);
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [streamState, setStreamState] = useState<StreamState>('idle');
  const [mediaError, setMediaError] = useState<MediaErrorInfo | null>(null);
  const [peerConnection, setPeerConnection] =
    useState<RTCPeerConnection | null>(null);
  /**
   * The most recent constraints passed to `startMediaStream`, so that
   * `retryMediaStream` and the `switchDevices` rollback path can re-run
   * getUserMedia with the same device selection without plumbing the
   * constraints back through every caller.
   */
  const lastMediaConstraintsRef = useRef<MediaStreamConstraints | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const peerConnectionIceProfileRef = useRef<string | null>(null);

  // Fingerprint the URLs list to rebuild the Peer Connection if they change
  const peerConnectionIceUrlsRef = useRef<string | null>(null);
  const disconnectedGraceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionStartedAtRef = useRef<number | null>(null);
  const hasReportedTerminalFailureRef = useRef(false);
  const candidateCountersRef = useRef<CandidateCounters>(
    emptyCandidateCounters(),
  );
  const gatheringStartedAtRef = useRef<number | null>(null);
  const iceCheckStartedAtRef = useRef<number | null>(null);
  const hasLoggedIceConnectedRef = useRef(false);
  const answerReceivedRef = useRef(false);
  const preConnectWatchdogRef = useRef<NodeJS.Timeout | null>(null);
  /**
   * Pointer to the per-PeerConnection `handleTerminalConnectionFailure`
   * closure so `createOffer`'s pre-connect watchdog can invoke it
   * without duplicating the stats-dump / onConnectionFailure wiring.
   * Rewritten inside `createPeerConnection` for each new PC.
   */
  const terminalFailureHandlerRef = useRef<
    (reason: ConnectionFailureReason) => void
  >(() => {});

  const clearPreConnectWatchdog = useCallback(() => {
    if (preConnectWatchdogRef.current) {
      clearTimeout(preConnectWatchdogRef.current);
      preConnectWatchdogRef.current = null;
    }
  }, []);

  /**
   * These refs are used to store the video elements so that we can
   * connect the local media stream to the local video element and
   * the remote media stream to the remote video element.
   */
  const videoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  const clearDisconnectedGraceTimeout = useCallback(() => {
    if (disconnectedGraceTimeoutRef.current) {
      clearTimeout(disconnectedGraceTimeoutRef.current);
      disconnectedGraceTimeoutRef.current = null;
    }
  }, []);

  const getConnectionFailureContext = useCallback(
    (peerConnection: RTCPeerConnection): ConnectionFailureContext => ({
      iceConnectionState: peerConnection.iceConnectionState,
      peerConnectionState: peerConnection.connectionState,
      signalingState: peerConnection.signalingState,
      iceGatheringState: peerConnection.iceGatheringState,
      elapsedMsSinceOffer: connectionStartedAtRef.current
        ? Date.now() - connectionStartedAtRef.current
        : null,
    }),
    [],
  );

  /**
   * Abort any in-flight stream operation and return a fresh AbortSignal
   * for the new one. Centralising this keeps `startMediaStream` and
   * `switchDevices` from racing each other's getUserMedia calls.
   */
  const beginStreamOperation = useCallback((): AbortSignal => {
    streamOperationAborterRef.current?.abort();
    streamOperationAborterRef.current = new AbortController();
    return streamOperationAborterRef.current.signal;
  }, []);

  const replaceOutgoingVideoTrack = useCallback<ReplaceOutgoingVideoTrack>(
    async (stream, videoTrack, signal) => {
      throwIfBlurAborted(signal);
      const sender = peerConnectionRef.current
        ?.getSenders()
        .find((candidate) => candidate.track?.kind === 'video');
      if (sender) {
        await waitForOperation(
          sender.replaceTrack(videoTrack),
          signal,
          5000,
          'Timed out replacing the outgoing video track',
        );
      }
      throwIfBlurAborted(signal);
      outboundStreamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    },
    [],
  );

  const {
    backgroundBlurEnabled,
    backgroundBlurError,
    backgroundBlurManagedBySystem,
    backgroundBlurMode,
    backgroundBlurStatus,
    setBackgroundBlurEnabled,
    prepareStream: prepareBackgroundBlurForStream,
    reset: resetBackgroundBlur,
  } = useBackgroundBlur(rawStreamRef, replaceOutgoingVideoTrack);

  /**
   * Release the currently-held MediaStream and detach it from the
   * local video element and peer connection. Cancels blur work before releasing
   * the camera so pending work cannot reattach an obsolete stream.
   */
  const releaseLiveStream = useCallback(() => {
    resetBackgroundBlur();

    const tracks = rawStreamRef.current?.getTracks() || [];
    tracks.forEach((track) => track.stop());

    if (peerConnectionRef.current) {
      peerConnectionRef.current.getSenders().forEach((sender) => {
        if (sender.track) {
          peerConnectionRef.current?.removeTrack(sender);
        }
      });
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    rawStreamRef.current = null;
    outboundStreamRef.current = null;
  }, [resetBackgroundBlur]);

  /**
   * Low-level primitive: acquire a MediaStream with the given constraints
   * without attaching it to the preview or peer connection. Deliberately
   * does **not** touch `mediaError`, `streamState`, the live stream refs, or
   * `lastMediaConstraintsRef` so it can be composed by
   * multiple callers (e.g. `switchDevices`, which wants to try the new
   * device and fall back to the previous one without flickering
   * `mediaError` in between).
   *
   * Returns the stream on success, or `null` if the operation was
   * aborted (in which case any acquired tracks have been stopped).
   * Throws if `getUserMedia` itself rejects.
   */
  const acquireStream = useCallback(
    async (
      mediaConstraints: MediaStreamConstraints,
      signal: AbortSignal,
    ): Promise<MediaStream | null> => {
      if (signal.aborted) return null;

      const mediaStream =
        await navigator.mediaDevices.getUserMedia(mediaConstraints);

      if (signal.aborted) {
        mediaStream.getTracks().forEach((track) => track.stop());
        return null;
      }

      return mediaStream;
    },
    [],
  );

  /** Install one acquired stream for startup, device switching, and rollback. */
  const activateStream = useCallback(
    async (
      stream: MediaStream,
      constraints: MediaStreamConstraints,
      signal: AbortSignal,
    ): Promise<boolean> => {
      if (signal.aborted) {
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }
      rawStreamRef.current = stream;
      outboundStreamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      if (peerConnectionRef.current) {
        stream
          .getTracks()
          .forEach((track) =>
            peerConnectionRef.current?.addTrack(track, stream),
          );
      }
      lastMediaConstraintsRef.current = constraints;
      await prepareBackgroundBlurForStream(stream, signal);
      if (signal.aborted || rawStreamRef.current !== stream) return false;
      setIsStreamReady(true);
      setStreamState('active');
      setMediaError(null);
      return true;
    },
    [prepareBackgroundBlurForStream],
  );

  /**
   * Start the media stream and add it to the peer connection. Thin
   * wrapper over `acquireStream` that owns the stateful bookkeeping:
   * clears `mediaError` on entry, records the constraints only on
   * success, and transitions `streamState` appropriately.
   */
  const startMediaStream = useCallback(
    async (
      mediaConstraints: MediaStreamConstraints = {
        video: true,
        audio: true,
      },
    ): Promise<MediaStream | null> => {
      const signal = beginStreamOperation();
      setStreamState('starting');
      setIsStreamReady(false);
      setMediaError(null);

      try {
        const mediaStream = await acquireStream(mediaConstraints, signal);

        if (signal.aborted || !mediaStream) return null;
        return (await activateStream(mediaStream, mediaConstraints, signal))
          ? mediaStream
          : null;
      } catch (error) {
        if (signal.aborted) return null;
        wlog('error', 'getUserMedia failed when starting stream', {
          component: 'use-webrtc-media',
          media_error_name: error instanceof Error ? error.name : 'Unknown',
          media_error_message:
            error instanceof Error ? error.message : String(error),
        });
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'video-calling');
          scope.setTag('component', 'use-webrtc-media');
          const callId = getCallId();
          if (callId) scope.setTag('call_id', callId);
          if (error instanceof Error) {
            scope.setTag('media_error_name', error.name);
          }
          Sentry.captureException(error);
        });
        setMediaError(getUserMediaErrorInfo(error));
        setStreamState('error');
        throw error;
      }
    },
    [acquireStream, activateStream, beginStreamOperation],
  );

  /**
   * Retry the most recent successful startMediaStream call. Because
   * `lastMediaConstraintsRef` only advances on success, this always
   * targets a last-known-good device configuration — even if the last
   * user action was a failed device switch.
   */
  const retryMediaStream = useCallback(async () => {
    const constraints = lastMediaConstraintsRef.current ?? {
      video: true,
      audio: true,
    };
    try {
      await startMediaStream(constraints);
    } catch {
      /**
       * startMediaStream already records the error state and reports to
       * Sentry. Swallow here so consumers wiring this to a button onClick
       * don't get an unhandled rejection.
       */
    }
  }, [startMediaStream]);

  /**
   * Switch to a new set of media constraints. On failure, attempt to
   * restore the previous working constraints so the user is never left
   * with a black screen.
   *
   * Throws a {@link MediaSwitchError} on failure. The `rolledBack` flag
   * on the error tells the caller whether the live stream is still
   * usable (true) or genuinely broken (false). In the rollback-success
   * case, `mediaError` is deliberately **not** set — the stream is fine
   * and the caller is expected to surface a toast for the transient
   * switch failure.
   */
  const switchDevices = useCallback(
    async (constraints: MediaStreamConstraints) => {
      const previousConstraints = lastMediaConstraintsRef.current;
      const signal = beginStreamOperation();

      /**
       * Tear down the current stream before acquiring the new one.
       * Most platforms can't have two capture sessions on the same
       * physical device at once, and this matches the pre-existing
       * single-owner semantics.
       */
      setStreamState('stopping');
      releaseLiveStream();
      setIsStreamReady(false);

      // Attempt 1: the constraints the caller asked for.
      let originalError: unknown;
      try {
        const newStream = await acquireStream(constraints, signal);
        if (signal.aborted) return;
        if (newStream) {
          await activateStream(newStream, constraints, signal);
          return;
        }
        originalError = new Error('Failed to start new media stream');
      } catch (error) {
        originalError = error;
      }

      if (signal.aborted) return;

      wlog('error', 'Device switch failed', {
        component: 'use-webrtc-media',
        media_error_name:
          originalError instanceof Error ? originalError.name : 'Unknown',
        media_error_message:
          originalError instanceof Error
            ? originalError.message
            : String(originalError),
      });
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'use-webrtc-media');
        const callId = getCallId();
        if (callId) scope.setTag('call_id', callId);
        if (originalError instanceof Error) {
          scope.setTag('media_error_name', originalError.name);
        }
        Sentry.captureException(originalError);
      });

      // Attempt 2: rollback to the previous constraints, if any.
      const canRollback =
        previousConstraints !== null && previousConstraints !== constraints;
      let rollbackSucceeded = false;
      if (canRollback) {
        try {
          const rollbackStream = await acquireStream(
            previousConstraints,
            signal,
          );
          if (!signal.aborted && rollbackStream) {
            rollbackSucceeded = await activateStream(
              rollbackStream,
              previousConstraints,
              signal,
            );
          }
        } catch (rollbackError) {
          wlog('error', 'Device rollback also failed after switch failure', {
            component: 'use-webrtc-media',
            rollback_error: true,
            media_error_name:
              rollbackError instanceof Error ? rollbackError.name : 'Unknown',
            media_error_message:
              rollbackError instanceof Error
                ? rollbackError.message
                : String(rollbackError),
          });
          Sentry.withScope((scope) => {
            scope.setTag('feature', 'video-calling');
            scope.setTag('component', 'use-webrtc-media');
            scope.setTag('rollback_error', 'true');
            const callId = getCallId();
            if (callId) scope.setTag('call_id', callId);
            if (rollbackError instanceof Error) {
              scope.setTag('media_error_name', rollbackError.name);
            }
            Sentry.captureException(rollbackError);
          });
        }
      }

      if (signal.aborted) return;

      if (!rollbackSucceeded) {
        /**
         * Either there was nothing to roll back to, or rollback itself
         * failed. The live stream is broken, so surface the overlay via
         * `mediaError` and put the hook into the error state.
         */
        setMediaError(getUserMediaErrorInfo(originalError));
        setStreamState('error');
      }

      throw new MediaSwitchError(originalError, rollbackSucceeded);
    },
    [acquireStream, activateStream, beginStreamOperation, releaseLiveStream],
  );

  const createPeerConnection = useCallback(() => {
    const nextProfile = iceOptions.iceProfileId ?? null;
    const nextUrls = iceServersFingerprint(iceOptions.iceServers);
    if (peerConnectionRef.current) {
      const canReplaceBeforeOffer = !peerConnectionRef.current.localDescription;
      const profileChanged =
        peerConnectionIceProfileRef.current !== nextProfile;
      const urlsChanged = peerConnectionIceUrlsRef.current !== nextUrls;
      if (canReplaceBeforeOffer && (profileChanged || urlsChanged)) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
        setPeerConnection(null);
      } else {
        return peerConnectionRef.current;
      }
    }

    try {
      /**
       * ASP does not accept Trickle ICE, so `use-call` pre-selects a
       * complete ICE profile before we create the real peer connection.
       * Avoid adding extra servers here: slow/blocked servers can delay
       * non-trickle gathering and cause us to send a weak partial offer.
       */
      const configuration: RTCConfiguration = {
        iceServers: iceOptions.iceServers,
        iceTransportPolicy: iceOptions.iceTransportPolicy,
      };
      const peerConnection = new RTCPeerConnection(configuration);
      peerConnectionRef.current = peerConnection;
      peerConnectionIceProfileRef.current = nextProfile;
      peerConnectionIceUrlsRef.current = nextUrls;
      setPeerConnection(peerConnection);
      hasReportedTerminalFailureRef.current = false;
      hasLoggedIceConnectedRef.current = false;
      candidateCountersRef.current = emptyCandidateCounters();
      gatheringStartedAtRef.current = null;
      iceCheckStartedAtRef.current = null;
      answerReceivedRef.current = false;
      clearDisconnectedGraceTimeout();
      clearPreConnectWatchdog();

      wlog(
        'info',
        fmt`Creating peer connection using ${iceOptions.iceProfileId ?? 'unknown'} ICE profile`,
        {
          component: 'use-webrtc-media',
          ice_profile_id: iceOptions.iceProfileId ?? null,
          ice_server_count: iceOptions.iceServers.length,
          ice_transport_policy: iceOptions.iceTransportPolicy ?? 'all',
        },
      );

      /**
       * We need to handle ICE gathering state changes
       * to know when we have finished gathering ICE candidates.
       * We will set a timeout to ensure we don't wait indefinitely.
       * If the ICE gathering takes too long, we will proceed with the offer
       * even if we haven't received all candidates.
       */
      let iceGatheringTimeout: NodeJS.Timeout;
      let isIceGatheringComplete = false;

      const handleIceGatheringComplete = () => {
        /**
         * This flag is used to ensure that we only handle the ICE gathering
         * complete event once, even if it is called multiple times.
         * This can happen if the ICE gathering state changes multiple times
         * before we handle the event.
         * If we have already handled the ICE gathering complete event,
         * we don't need to do anything.
         */
        if (isIceGatheringComplete) return;
        isIceGatheringComplete = true;

        if (iceGatheringTimeout) {
          clearTimeout(iceGatheringTimeout);
        }

        const elapsedMs = gatheringStartedAtRef.current
          ? Math.round(performance.now() - gatheringStartedAtRef.current)
          : null;
        const counters = candidateCountersRef.current;
        wlog('info', fmt`ICE gathering complete in ${elapsedMs ?? 0}ms`, {
          component: 'use-webrtc-media',
          elapsed_ms: elapsedMs,
          candidate_total: counters.total,
          candidate_host: counters.host,
          candidate_srflx: counters.srflx,
          candidate_prflx: counters.prflx,
          candidate_relay: counters.relay,
          protocol_udp: counters.udp,
          protocol_tcp: counters.tcp,
        });

        const fullOffer = peerConnection.localDescription;
        if (fullOffer && onOfferReady) {
          onOfferReady(fullOffer);
        }
      };

      const handleIceGatheringTimeout = () => {
        /**
         * This flag is used to ensure that we only handle the ICE gathering
         * timeout event once, even if it is called multiple times, and
         * that if ice gathering subsequently completes,
         * we indicate that we have already handled it.
         */
        if (isIceGatheringComplete) return;
        isIceGatheringComplete = true;

        const partialOffer = peerConnection.localDescription;
        if (partialOffer && onOfferReady) {
          const elapsedMs = gatheringStartedAtRef.current
            ? Math.round(performance.now() - gatheringStartedAtRef.current)
            : null;
          const counters = candidateCountersRef.current;
          wlog(
            'error',
            'ICE gathering timed out, proceeding with partial offer',
            {
              component: 'use-webrtc-media',
              elapsed_ms: elapsedMs,
              candidate_total: counters.total,
              candidate_host: counters.host,
              candidate_srflx: counters.srflx,
              candidate_prflx: counters.prflx,
              candidate_relay: counters.relay,
              protocol_udp: counters.udp,
              protocol_tcp: counters.tcp,
            },
          );
          onOfferReady(partialOffer);
        }
      };

      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          tallyCandidate(candidateCountersRef.current, event.candidate);
        }
      };

      peerConnection.onicecandidateerror = (event) => {
        const e = event as RTCPeerConnectionIceErrorEvent;
        wlog('warn', fmt`ICE candidate error ${e.errorCode}: ${e.errorText}`, {
          component: 'use-webrtc-media',
          error_code: e.errorCode,
          error_text: e.errorText,
          // `url` is the STUN/TURN server URL the candidate was being
          // gathered against — diagnostic, not user PII.
          stun_turn_url: e.url,
          port: e.port,
        });
      };

      /**
       * Once we have finished gathering ICE candidates, we will send the offer
       * via the onOfferReady callback that is passed in to this hook
       */
      peerConnection.onicegatheringstatechange = () => {
        const state = peerConnection.iceGatheringState;
        if (state === 'gathering') {
          gatheringStartedAtRef.current = performance.now();
          wlog('info', 'ICE gathering started', {
            component: 'use-webrtc-media',
          });
          iceGatheringTimeout = setTimeout(handleIceGatheringTimeout, 10000);
        } else if (state === 'complete') {
          handleIceGatheringComplete();
        }
      };

      // Await the stats extraction and emit the log *before* invoking
      // `onConnectionFailure`, because the callback synchronously tears
      // down the call: it closes `peerConnection` (making `getStats()`
      // unreliable) and clears the logger's current-call context
      // (stripping call_id/unit_id from the log we care about most).
      const handleTerminalConnectionFailure = async (
        reason: ConnectionFailureReason,
      ) => {
        if (hasReportedTerminalFailureRef.current) {
          return;
        }
        hasReportedTerminalFailureRef.current = true;
        clearDisconnectedGraceTimeout();
        clearPreConnectWatchdog();
        const context = getConnectionFailureContext(peerConnection);
        const counters = candidateCountersRef.current;

        const attemptedPairs = await extractAttemptedPairs(peerConnection);
        wlog('error', fmt`WebRTC terminal connection failure: ${reason}`, {
          component: 'use-webrtc-media',
          failure_reason: reason,
          ice_connection_state: context.iceConnectionState,
          peer_connection_state: context.peerConnectionState,
          signaling_state: context.signalingState,
          ice_gathering_state: context.iceGatheringState,
          elapsed_ms_since_offer: context.elapsedMsSinceOffer,
          answer_received: answerReceivedRef.current,
          candidate_total: counters.total,
          candidate_host: counters.host,
          candidate_srflx: counters.srflx,
          candidate_relay: counters.relay,
          attempted_pair_count: attemptedPairs.length,
          attempted_pairs: attemptedPairs,
        });

        onConnectionFailure?.(reason, context);
      };
      terminalFailureHandlerRef.current = handleTerminalConnectionFailure;

      peerConnection.oniceconnectionstatechange = () => {
        const state = peerConnection.iceConnectionState;

        if (state === 'checking') {
          iceCheckStartedAtRef.current = performance.now();
        }

        wlog('info', fmt`ICE connection state: ${state}`, {
          component: 'use-webrtc-media',
          ice_connection_state: state,
          ice_gathering_state: peerConnection.iceGatheringState,
          signaling_state: peerConnection.signalingState,
        });

        if (state === 'disconnected') {
          clearDisconnectedGraceTimeout();
          disconnectedGraceTimeoutRef.current = setTimeout(() => {
            handleTerminalConnectionFailure('ice_disconnected_timeout');
          }, ICE_DISCONNECT_GRACE_MS);
          return;
        }

        if (state === 'connected' || state === 'completed') {
          clearDisconnectedGraceTimeout();
          clearPreConnectWatchdog();
          if (!hasLoggedIceConnectedRef.current) {
            hasLoggedIceConnectedRef.current = true;
            const elapsedMs = iceCheckStartedAtRef.current
              ? Math.round(performance.now() - iceCheckStartedAtRef.current)
              : null;
            void extractSelectedPair(peerConnection).then((pair) => {
              wlog(
                'info',
                fmt`ICE connected in ${elapsedMs ?? 0}ms via ${pair?.local_type ?? 'unknown'}/${pair?.remote_type ?? 'unknown'} ${pair?.local_protocol ?? ''}`,
                {
                  component: 'use-webrtc-media',
                  elapsed_ms: elapsedMs,
                  ice_connection_state: state,
                  ...(pair ?? {}),
                },
              );
            });
          }
          return;
        }

        if (state === 'failed') {
          handleTerminalConnectionFailure('ice_failed');
          return;
        }

        if (state === 'closed') {
          clearDisconnectedGraceTimeout();
          clearPreConnectWatchdog();
        }
      };

      peerConnection.onconnectionstatechange = () => {
        const state = peerConnection.connectionState;
        wlog('info', fmt`Peer connection state: ${state}`, {
          component: 'use-webrtc-media',
          peer_connection_state: state,
          ice_connection_state: peerConnection.iceConnectionState,
        });

        if (state === 'failed') {
          handleTerminalConnectionFailure('pc_failed');
          return;
        }

        if (state === 'connected') {
          clearDisconnectedGraceTimeout();
          clearPreConnectWatchdog();
        }

        if (state === 'closed') {
          clearDisconnectedGraceTimeout();
          clearPreConnectWatchdog();
        }
      };

      /**
       * This is called when we receive a remote media track.
       * Accumulate incoming tracks into a single MediaStream on the video
       * element — React Native WebRTC (tablet side) can deliver audio/video
       * as separate streams, and relying on `event.streams[0]` alone would
       * cause the second track to replace (and hide) the first.
       */
      peerConnection.ontrack = (event) => {
        wlog('info', fmt`Remote ${event.track.kind} track received`, {
          component: 'use-webrtc-media',
          track_kind: event.track.kind,
          stream_count: event.streams.length,
        });
        if (!remoteVideoRef.current || !event.track) return;
        const existing = remoteVideoRef.current.srcObject as MediaStream | null;
        if (existing instanceof MediaStream) {
          const alreadyAdded = existing
            .getTracks()
            .some((t) => t.id === event.track.id);
          if (!alreadyAdded) {
            existing.addTrack(event.track);
          }
        } else {
          remoteVideoRef.current.srcObject =
            event.streams[0] ?? new MediaStream([event.track]);
        }
      };

      /**
       * If we have a local media stream, we add its tracks to the peer connection,
       * preventing a race condition where the peer connection is created
       * before the local media stream is started.
       */
      if (outboundStreamRef.current) {
        outboundStreamRef.current.getTracks().forEach((track) => {
          peerConnection.addTrack(track, outboundStreamRef.current!);
        });
      }

      return peerConnection;
    } catch (error) {
      wlog('error', 'Failed to create peer connection', {
        component: 'use-webrtc-media',
        error_name: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : String(error),
      });
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'use-webrtc-media');
        const callId = getCallId();
        if (callId) scope.setTag('call_id', callId);
        Sentry.captureException(error);
      });
      return null;
    }
  }, [
    clearDisconnectedGraceTimeout,
    clearPreConnectWatchdog,
    getConnectionFailureContext,
    iceOptions,
    onConnectionFailure,
    onOfferReady,
  ]);

  /**
   * This function creates a WebRTC offer.
   */
  const createOffer = useCallback(async () => {
    if (!peerConnectionRef.current) {
      wlog('error', 'Attempted to create offer without peer connection', {
        component: 'use-webrtc-media',
      });
      return;
    }

    const offerStartedAt = performance.now();
    try {
      connectionStartedAtRef.current = Date.now();
      const localOffer = await peerConnectionRef.current.createOffer({
        offerToReceiveVideo: true,
        offerToReceiveAudio: true,
      });

      await peerConnectionRef.current.setLocalDescription(localOffer);
      wlog(
        'info',
        fmt`SDP offer created in ${Math.round(performance.now() - offerStartedAt)}ms`,
        {
          component: 'use-webrtc-media',
          elapsed_ms: Math.round(performance.now() - offerStartedAt),
          sdp_length: localOffer.sdp?.length ?? 0,
        },
      );
      // Note: the pre-connect watchdog starts in `handleAnswer` after
      // `setRemoteDescription`, not here. During ringing (before the
      // remote picks up) `iceConnectionState` stays in `'new'`, so a
      // watchdog started at offer time would misfire for any call that
      // rings longer than PRE_CONNECT_WATCHDOG_MS. We only want to cap
      // the "checking but no route found" window.
    } catch (error) {
      connectionStartedAtRef.current = null;
      wlog('error', 'Failed to create SDP offer', {
        component: 'use-webrtc-media',
        error_name: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : String(error),
        elapsed_ms: Math.round(performance.now() - offerStartedAt),
      });
      Sentry.withScope((scope) => {
        scope.setTag('feature', 'video-calling');
        scope.setTag('component', 'use-webrtc-media');
        const callId = getCallId();
        if (callId) scope.setTag('call_id', callId);
        Sentry.captureException(error);
      });
      throw error;
    }
  }, []);

  /**
   * Apply a remote SDP answer (from either Alexa or a Hexnode tablet),
   * flip `answerReceivedRef`, and start the pre-connect watchdog.
   *
   * Caller is responsible for parsing the device-specific message shape
   * and short-circuiting on duplicate answers; this helper assumes its
   * inputs are already validated and unique.
   */
  const applyRemoteAnswer = useCallback(
    async (
      type: RTCSdpType,
      sdp: string,
      source: 'alexa' | 'tablet',
    ): Promise<boolean> => {
      if (!peerConnectionRef.current) {
        wlog('error', 'Received answer but no peer connection exists', {
          component: 'use-webrtc-media',
          answer_source: source,
        });
        return false;
      }

      const applyStartedAt = performance.now();
      try {
        await peerConnectionRef.current.setRemoteDescription(
          new RTCSessionDescription({ type, sdp }),
        );
        answerReceivedRef.current = true;
        wlog(
          'info',
          fmt`SDP answer applied (${source}) in ${Math.round(performance.now() - applyStartedAt)}ms`,
          {
            component: 'use-webrtc-media',
            answer_source: source,
            sdp_type: type,
            sdp_length: sdp.length,
            elapsed_ms: Math.round(performance.now() - applyStartedAt),
          },
        );

        // Start the pre-connect watchdog now that the remote SDP is
        // applied. From here ICE starts connectivity checks; if none of
        // them succeed within PRE_CONNECT_WATCHDOG_MS we synthesise a
        // `connection_timeout` so the failure is captured with attempted-
        // pair stats before the user gives up and silently closes the
        // modal. Starting it here (rather than at offer time) means ring
        // time is unbounded — users can let a call ring as long as they
        // like without triggering a spurious timeout.
        clearPreConnectWatchdog();
        preConnectWatchdogRef.current = setTimeout(() => {
          preConnectWatchdogRef.current = null;
          const pc = peerConnectionRef.current;
          if (!pc) return;
          const iceState = pc.iceConnectionState;
          if (iceState === 'new' || iceState === 'checking') {
            void terminalFailureHandlerRef.current('connection_timeout');
          }
        }, PRE_CONNECT_WATCHDOG_MS);
        return true;
      } catch (error) {
        wlog('error', 'Failed to set remote SDP answer', {
          component: 'use-webrtc-media',
          answer_source: source,
          sdp_type: type,
          error_name: error instanceof Error ? error.name : 'Unknown',
          error_message: error instanceof Error ? error.message : String(error),
        });
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'video-calling');
          scope.setTag('component', 'use-webrtc-media');
          const callId = getCallId();
          if (callId) scope.setTag('call_id', callId);
          scope.setExtra('sdp_type', type);
          scope.setExtra('answer_source', source);
          Sentry.captureException(error);
        });
        return false;
      }
    },
    [clearPreConnectWatchdog],
  );

  const handleAnswer = useCallback(
    async (answer: CallSdpMessage) => {
      if (!peerConnectionRef.current) {
        wlog('error', 'Received answer but no peer connection exists', {
          component: 'use-webrtc-media',
        });
        return false;
      }

      const sdpDetails = answer.body?.sdpDetails;

      if (!sdpDetails || !sdpDetails.type || !sdpDetails.value) {
        if (answerReceivedRef.current) {
          wlog(
            'info',
            'Received call message without SDP after answer applied',
            {
              component: 'use-webrtc-media',
              message_action: answer.action,
            },
          );
          return true;
        }
        wlog('warn', 'Received call message without usable SDP answer', {
          component: 'use-webrtc-media',
          message_action: answer.action,
          has_body: !!answer.body,
          has_sdp_details: !!sdpDetails,
          has_sdp_type: !!sdpDetails?.type,
          has_sdp_value: !!sdpDetails?.value,
        });
        return false;
      }

      const sdpType = sdpDetails.type.toLowerCase();
      if (
        sdpType !== 'answer' &&
        sdpType !== 'offer' &&
        sdpType !== 'pranswer' &&
        sdpType !== 'rollback'
      ) {
        wlog('error', fmt`Received invalid SDP type: ${sdpDetails.type}`, {
          component: 'use-webrtc-media',
          sdp_type: sdpDetails.type,
        });
        return false;
      }

      if (answerReceivedRef.current) {
        wlog('info', 'Ignoring duplicate SDP answer', {
          component: 'use-webrtc-media',
          message_action: answer.action,
          sdp_type: sdpDetails.type,
        });
        return true;
      }

      return applyRemoteAnswer(
        sdpType as RTCSdpType,
        sdpDetails.value,
        'alexa',
      );
    },
    [applyRemoteAnswer],
  );

  /**
   * Handle an answer from a tablet device. The tablet answer format
   * differs from the Alexa format: `{ action: 'answer', answer: { sdp } }`
   * instead of `{ action: 'callAccepted', body: { sdpDetails: { type, value } } }`.
   *
   * The tablet sends its answer as soon as `setLocalDescription` completes
   * and trickles its ICE candidates afterwards over the WebSocket. The
   * `applyRemoteAnswer` helper starts the pre-connect watchdog so that a
   * call which never reaches `connected` still surfaces a terminal
   * failure; the caller in `use-call.ts` is responsible for flushing
   * `pendingRemoteCandidatesRef` after we return.
   */
  const handleTabletAnswer = useCallback(
    async (message: TabletAnswerMessage): Promise<boolean> => {
      if (!peerConnectionRef.current) {
        wlog('error', 'Received tablet answer but no peer connection exists', {
          component: 'use-webrtc-media',
        });
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'video-calling');
          scope.setTag('component', 'use-webrtc-media');
          Sentry.captureMessage(
            'WebRTC: Received tablet answer but no peer connection exists',
            'error',
          );
        });
        return false;
      }

      if (!message.answer?.sdp) {
        wlog('warn', 'Received tablet answer without usable SDP', {
          component: 'use-webrtc-media',
          has_answer: !!message.answer,
          has_sdp: !!message.answer?.sdp,
        });
        Sentry.withScope((scope) => {
          scope.setTag('feature', 'video-calling');
          scope.setTag('component', 'use-webrtc-media');
          scope.setContext('answer_metadata', {
            hasAnswer: !!message.answer,
            hasSdp: !!message.answer?.sdp,
          });
          Sentry.captureMessage(
            'WebRTC: Received invalid tablet answer object',
            'error',
          );
        });
        return false;
      }

      if (answerReceivedRef.current) {
        wlog('info', 'Ignoring duplicate tablet SDP answer', {
          component: 'use-webrtc-media',
          message_action: message.action,
        });
        return true;
      }

      // Count `a=candidate:` lines so dashboards can distinguish a true
      // trickle answer (0 baked-in candidates, candidates arrive later
      // as ice-candidate messages) from a tablet that still gathered
      // before answering. Cheap split — SDP is at most a few KB.
      const bakedInCandidates = message.answer.sdp
        .split('\n')
        .filter((line) => line.startsWith('a=candidate:')).length;
      wlog(
        'info',
        fmt`Received tablet SDP answer with ${bakedInCandidates} baked-in ICE candidate(s)`,
        {
          component: 'use-webrtc-media',
          baked_in_candidate_total: bakedInCandidates,
          sdp_length: message.answer.sdp.length,
        },
      );

      return applyRemoteAnswer('answer', message.answer.sdp, 'tablet');
    },
    [applyRemoteAnswer],
  );

  const cleanup = useCallback(() => {
    streamOperationAborterRef.current?.abort();
    resetBackgroundBlur(true);
    clearDisconnectedGraceTimeout();
    clearPreConnectWatchdog();
    connectionStartedAtRef.current = null;
    hasReportedTerminalFailureRef.current = false;
    answerReceivedRef.current = false;

    const tracks = rawStreamRef.current?.getTracks() || [];
    tracks.forEach((track) => {
      track.stop();
    });

    if (peerConnectionRef.current) {
      const senders = peerConnectionRef.current.getSenders();
      senders.forEach((sender) => {
        if (sender.track) {
          peerConnectionRef.current?.removeTrack(sender);
        }
      });
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
      peerConnectionIceProfileRef.current = null;
      peerConnectionIceUrlsRef.current = null;
      setPeerConnection(null);
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }

    rawStreamRef.current = null;
    outboundStreamRef.current = null;
    lastMediaConstraintsRef.current = null;
    setIsStreamReady(false);
    setStreamState('idle');
    setMediaError(null);
  }, [
    clearDisconnectedGraceTimeout,
    clearPreConnectWatchdog,
    resetBackgroundBlur,
  ]);

  const getPeerConnection = useCallback(() => peerConnectionRef.current, []);
  const getAnswerReceived = useCallback(() => answerReceivedRef.current, []);
  const getLocalCandidateCounts = useCallback(
    () => candidateCountersRef.current,
    [],
  );

  /**
   * Attach the cleanup function, so it gets called when the component unmounts.
   */
  useEffect(() => () => cleanup(), [cleanup]);

  // Effect to handle isActive state changes
  useEffect(() => {
    if (isActive) {
      /**
       * startMediaStream records errors on `mediaError` and Sentry
       * itself; swallow the rejection here so the floating promise
       * doesn't produce an unhandled rejection.
       */
      startMediaStream().catch(() => {});
    } else {
      // When modal closes, fully cleanup including peer connection
      cleanup();
    }
  }, [isActive, startMediaStream, cleanup]);

  return useMemo(
    () => ({
      isStreamReady,
      streamState,
      videoRef,
      switchDevices,
      backgroundBlurEnabled,
      backgroundBlurError,
      backgroundBlurManagedBySystem,
      backgroundBlurMode,
      backgroundBlurStatus,
      setBackgroundBlurEnabled,
      mediaError,
      retryMediaStream,

      // WebRTC connection
      peerConnection,
      remoteVideoRef,
      getPeerConnection,
      createPeerConnection,
      createOffer,
      handleAnswer,
      handleTabletAnswer,
      getAnswerReceived,
      getLocalCandidateCounts,

      cleanup,
    }),
    [
      cleanup,
      backgroundBlurEnabled,
      backgroundBlurError,
      backgroundBlurManagedBySystem,
      backgroundBlurMode,
      backgroundBlurStatus,
      createOffer,
      createPeerConnection,
      getAnswerReceived,
      getLocalCandidateCounts,
      getPeerConnection,
      handleAnswer,
      handleTabletAnswer,
      isStreamReady,
      mediaError,
      peerConnection,
      retryMediaStream,
      setBackgroundBlurEnabled,
      streamState,
      switchDevices,
    ],
  );
}
